import * as React from "react"
import { useMutation } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { CheckCircle2, ShieldCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useAuth } from "@/lib/auth"
import { ApiError } from "@/lib/api"

import { aceptarInvitacion } from "@/routes/accesos/api"
import { t } from "@/lib/i18n"

/**
 * Aceptar una invitacion.
 *
 * LAS DOS MITADES SE ATAN AQUI. El enlace dice A QUE se te invito —que cliente,
 * que rol, que alcance, decidido por quien te invito— y Firebase dice QUIEN
 * eres. El backend comprueba que el correo de la sesion sea el de la
 * invitacion: si no, una invitacion filtrada le serviria a cualquiera.
 *
 * POR QUE ESTA RUTA EXIGE SESION Y AUN ASI NO ESTA DENTRO DE LA APP. Quien
 * acepta todavia no tiene alcance sobre ningun cliente, asi que no puede pasar
 * por las pantallas normales —el selector de cliente estaria vacio y `RequireSeccion`
 * lo echaria—. Vive fuera del layout, con su propia explicacion.
 */
export function InvitacionPage() {
  const { token = "" } = useParams()
  const { firebaseUser, profile } = useAuth()
  const navigate = useNavigate()
  const [hecho, setHecho] = React.useState<{ rol: string; aviso: string } | null>(null)

  const aceptar = useMutation({
    mutationFn: () => aceptarInvitacion(token),
    onSuccess: (r) => setHecho({ rol: r.rol, aviso: r.aviso }),
  })

  const error = aceptar.error

  return (
    <div className="mx-auto max-w-lg px-6 py-16">
      <div className="rounded-panel border bg-surface p-6">
        <ShieldCheck className="h-6 w-6 text-brand" aria-hidden="true" />
        <h1 className="titular punto mt-3 text-lg">{t("Te invitaron a Marty")}</h1>

        {hecho ? (
          <>
            <p className="mt-2 flex items-start gap-1.5 text-xs leading-5">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>
                {t("Listo: entras con el rol")}{" "}<span className="code">{hecho.rol}</span>. {hecho.aviso}
              </span>
            </p>
            <Button
              className="mt-4"
              size="sm"
              onClick={() => {
                // Recarga entera y no navegacion: el alcance del usuario cambio
                // en el servidor, y el perfil que tiene esta pestana en memoria
                // es el de antes de aceptar. Navegar mostraria una aplicacion
                // vacia y pareceria que la invitacion no funciono.
                window.location.assign("/primeros-pasos")
              }}
            >
              {t("Entrar")}</Button>
          </>
        ) : !firebaseUser ? (
          <>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">
              {t("Para aceptarla necesitas iniciar sesión con la cuenta a la que se envió la invitación. Es la comprobación que impide que un enlace reenviado le sirva a otra persona.")}</p>
            <Button asChild className="mt-4" size="sm">
              <Link to={`/login?redirect=/invitacion/${token}`}>{t("Iniciar sesión")}</Link>
            </Button>
          </>
        ) : (
          <>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">
              {t("Estás como")}{" "}<span className="code">{profile?.email ?? firebaseUser.email}</span>{t(". Al aceptar, quedarás dado de alta con el rol que decidió quien te invitó.")}</p>
            {error ? (
              <p className="mt-3 rounded-md border border-destructive/30 bg-destructive-soft px-2.5 py-2 text-2xs leading-5 text-destructive">
                {error instanceof ApiError && typeof error.detail === "string"
                  ? error.detail
                  : "No se pudo aceptar la invitación. Puede que haya vencido, que ya se haya usado, o que sea para otra cuenta."}
              </p>
            ) : null}
            <div className="mt-4 flex gap-2">
              <Button size="sm" disabled={aceptar.isPending} onClick={() => aceptar.mutate()}>
                {aceptar.isPending ? "Aceptando…" : "Aceptar la invitación"}
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/dashboard">{t("Ahora no")}</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
